import { useEffect, useState } from 'react';
import { isPreloadSearch } from '@/api/publicResolve';

/**
 * Событие, которым мобильный SDK сообщает «страница реально на экране».
 * Контракт с iOS SDK 0.8.0 (предзагрузка пейволла): в момент показа SDK
 * выполняет в странице
 *   window.__web2appShown = true;
 *   window.dispatchEvent(new Event('web2app:shown'));
 */
export const APP_SHOWN_EVENT = 'web2app:shown';

type ShownWindow = Window & { __web2appShown?: boolean };

function readShownFlag(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as ShownWindow).__web2appShown === true
  );
}

/**
 * Показана ли страница человеку — для страниц, которые SDK умеет грузить
 * заранее (`?preload=1`).
 *
 * ЗАЧЕМ. SDK грузит экран оплаты в фоновом WebView, чтобы открыть его
 * мгновенно. Всё, что страница считает «просмотром» (PAYWALL_VIEW, PageView
 * пикселей, замер скорости), при предзагрузке случилось бы в фоне — три
 * предзагруженных пейволла дали бы три просмотра, которых никто не видел.
 *
 * КАК. Без предзагрузки страница считается показанной сразу — обычные
 * заходы и приложения на старом SDK живут как раньше. С предзагрузкой ждём
 * сигнала: сперва флаг (сигнал мог прийти ДО монтирования React), потом
 * событие. Повторы сигнала (SDK шлёт его после каждой загрузки страницы на
 * экране) ничего не меняют: показ необратим, а «один раз» держит сам
 * потребитель. После возврата со Stripe сигнал не нужен вовсе: адреса
 * возврата собираются заново (PaywallScreenWizard) и `preload` не несут,
 * так что такая страница считается показанной сразу.
 */
export function useAppShownSignal(preload: boolean): boolean {
  const [shown, setShown] = useState(() => !preload || readShownFlag());

  useEffect(() => {
    if (!preload) {
      setShown(true);
      return;
    }
    if (readShownFlag()) {
      setShown(true);
      return;
    }
    // Запасной путь: первое действие человека (нажатие, клавиша, касание)
    // тоже считается показом. Без него приложение, которое показало
    // страницу, но сигнал не прислало (сбой SDK, старая сборка с
    // `preload=1`), оставило бы страницу «непоказанной» навсегда: просмотр,
    // пиксели и замер не ушли бы никогда, а «начало оплаты» ушло бы — и
    // доля шага «просмотр → оплата» перевалила бы за 100 %. Нажать на
    // страницу, которой не видно, человек не может, поэтому действие —
    // честное доказательство показа. Слушаем в фазе захвата, чтобы
    // остановка всплытия внутри страницы не спрятала действие; все
    // слушатели снимаются после первого срабатывания и при размонтировании.
    const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart'] as const;
    const detach = (): void => {
      window.removeEventListener(APP_SHOWN_EVENT, onShown);
      for (const type of INTERACTION_EVENTS) {
        window.removeEventListener(type, onShown, true);
      }
    };
    function onShown(): void {
      detach();
      // Показ — свойство страницы, а не одного компонента: встроенный в квиз
      // экран оплаты держит свой экземпляр хука и без флага ждал бы показа
      // заново (свежий взгляд по PR #26, волна 2). SDK ставит тот же флаг сам.
      (window as ShownWindow).__web2appShown = true;
      setShown(true);
    }
    window.addEventListener(APP_SHOWN_EVENT, onShown);
    for (const type of INTERACTION_EVENTS) {
      window.addEventListener(type, onShown, true);
    }
    return detach;
  }, [preload]);

  return shown;
}

/**
 * WEB-3673, переделка 2 — показана ли страница человеку, для всего, что
 * ОТСЧИТЫВАЕТ ВРЕМЯ на странице воронки: задержка кнопки закрытия, таймеры
 * обратного отсчёта, заставка с прогрессом, перенаправление с отсчётом,
 * автозапуск видео. Прогретая SDK страница (`?preload=1`) живёт в фоне до
 * часа и рисует содержимое заранее, но отсчёт обязан начинаться от показа
 * человеку, а не от фоновой загрузки — иначе кнопка закрытия видна сразу, а
 * таймер «15 минут» показывает 04:59 или 00:00.
 *
 * Одна дверь для всех блоков: признак прогрева — та же форма, что у резолва
 * (`isPreloadSearch`), сигнал — `useAppShownSignal` (флаг SDK, событие,
 * первое касание). Без `preload` — «показано сразу», как раньше. Адрес за
 * жизнь страницы не меняется — признак читается один раз.
 */
export function usePageShown(): boolean {
  const [preload] = useState(
    () =>
      typeof window !== 'undefined' && isPreloadSearch(window.location.search),
  );
  return useAppShownSignal(preload);
}
